import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SOURCE_TYPE_CONFIG } from './SourceChips';

/**
 * A right-side slide-out drawer that shows full details for historical sources
 * cited in an AI response. Matches the Bible Reader panel pattern.
 */
export default function SourcesDrawer({ sources, activeIndex, onClose }) {
  const { t } = useTranslation();
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeIndex]);

  if (!sources || sources.length === 0) return null;

  return (
    <div
      className="absolute top-0 right-0 h-full w-full lg:static lg:w-[420px] xl:w-[480px] z-30 border-l theme-border theme-surface shadow-xl lg:shadow-none flex flex-col"
      data-testid="sources-drawer"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b theme-border flex-shrink-0">
        <div>
          <h2 className="text-base font-semibold theme-text">{t('sources.title')}</h2>
          <p className="text-xs theme-text-secondary mt-0.5">
            {sources.length} {sources.length === 1 ? t('sources.referenced_single') : t('sources.referenced')}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 rounded-full hover:bg-[var(--theme-hover)]"
          data-testid="sources-drawer-close"
        >
          <X className="h-4 w-4 theme-text-secondary" />
        </Button>
      </div>

      {/* Source Cards */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {sources.map((source, i) => {
            const config = SOURCE_TYPE_CONFIG[source.source_type] || SOURCE_TYPE_CONFIG.isbe;
            const Icon = config.icon;
            const isActive = i === activeIndex;

            return (
              <div
                key={`${source.source_type}-${source.work_title}-${i}`}
                ref={isActive ? activeRef : null}
                className="rounded-2xl border p-5 transition-all duration-300"
                style={{
                  borderColor: isActive ? config.color : 'var(--theme-border)',
                  background: isActive ? config.bg : 'var(--theme-surface)',
                  boxShadow: isActive ? `0 0 0 1px ${config.color}40` : 'none',
                }}
                data-testid={`source-card-${i}`}
              >
                {/* Category badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                    style={{ background: config.bg, color: config.color }}
                  >
                    <Icon className="h-3 w-3" />
                    {t(config.labelKey)}
                  </span>
                  {source.era && (
                    <span className="text-[11px] theme-text-secondary">{source.era}</span>
                  )}
                </div>

                {/* Work title */}
                <h3 className="text-sm font-bold theme-text leading-snug mb-1">
                  {source.work_title}
                </h3>

                {/* Source name + section */}
                <div className="text-xs theme-text-secondary mb-3 space-y-0.5">
                  {source.source_name && (
                    <p>{source.source_name}</p>
                  )}
                  {source.section && (
                    <p className="opacity-70">{source.section}</p>
                  )}
                </div>

                {/* Bible reference */}
                {source.book && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <ExternalLink className="h-3 w-3" style={{ color: config.color }} />
                    <span className="text-xs font-medium" style={{ color: config.color }}>
                      {source.book}{source.chapter ? ` ${source.chapter}` : ''}
                    </span>
                  </div>
                )}

                {/* Full content */}
                <div className="text-sm theme-text leading-relaxed">
                  {source.content}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
